"use client"
import { memo, useState, useEffect, useRef, useCallback } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { ArrowRight } from 'lucide-react';
import Link from 'next/link';
import Section from '@/components/layout/Section';
import { getMenuData } from '@/app/lib/sanity';
import Image from 'next/image';
import { animations } from '@/app/styles/design-system';

const FeaturedProductItem = memo(({ item, index }) => {
  const imageUrl = item.imageUrl || item.image;
  const prefersReducedMotion = useReducedMotion();

  // Optimize animations based on user preferences
  const hoverAnimation = prefersReducedMotion ? {} : {
    scale: 1.1,
    rotate: [0, -2, 2, 0]
  };

  return (
    <Link href="/menu" className="block">
      <motion.div
        className="group cursor-pointer text-center w-[280px]"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ 
          opacity: 1, 
          y: 0,
          transition: {
            duration: 0.4,
            delay: Math.min(index * 0.1, 0.3), // Cap the delay
            ease: [0.23, 1, 0.32, 1]
          }
        }}
        whileHover={{ y: -5 }}
        viewport={{ once: true, margin: "50px" }}
      >
        <div className="relative aspect-square mb-4">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative w-[85%] h-[85%]">
              {/* Simplified glow effect using CSS */}
              <div 
                className="absolute inset-[-50%] z-0 transition-opacity duration-300"
                style={{
                  background: 'radial-gradient(circle at center, rgba(255,107,53,0.12) 0%, transparent 70%)',
                  filter: 'blur(20px)',
                  opacity: 0.5,
                }}
              />

              {/* Image */}
              <motion.div 
                className="relative z-10 w-full h-full transform-gpu"
                whileHover={hoverAnimation}
                transition={{ duration: 0.4 }}
              >
                <Image
                  src={imageUrl}
                  alt={item.name}
                  fill
                  className="object-contain will-change-transform"
                  sizes="280px"
                  priority={index < 3}
                />
              </motion.div>
            </div>
          </div>
        </div>
        
        <h3 className="text-lg font-medium bg-clip-text text-transparent bg-gradient-to-r from-primary-orange to-primary-light group-hover:from-primary-light group-hover:to-primary-orange transition-all duration-300">
          {item.name}
        </h3>
      </motion.div>
    </Link>
  );
});
FeaturedProductItem.displayName = 'FeaturedProductItem';

const FeaturedProducts = ({ noPaddingTop }) => {
  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  // Memoize the fetch function
  const fetchMenuData = useCallback(async () => {
    try {
      const data = await getMenuData();
      setMenuData(data);
    } catch (error) {
      console.error('Error fetching menu data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMenuData();
  }, [fetchMenuData]);

  // Memoize featured items
  const featuredItems = menuData?.mochiDonuts?.filter(item => item.available !== false).slice(0, 6) || [];

  if (loading) {
    return (
      <Section className="relative flex flex-col py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-12 h-12 bg-primary-orange/20 rounded-full mb-4"></div>
            <p className="text-neutral-600">Loading featured products...</p>
          </div>
        </div>
      </Section>
    );
  }

  return (
    <Section 
      className="relative flex flex-col py-16 md:py-20 -mb-1 overflow-hidden"
    >
      {/* Enhanced Layered Background */}
      <div className="absolute inset-0">
        {/* Primary gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-white via-[#FFF9F5] to-[#FFF6F0]" />

        {/* Animated patterns */}
        <div className="absolute inset-0">
          {/* Honeycomb pattern */}
          <div 
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: `
                linear-gradient(60deg, #FF8C61 1px, transparent 1px),
                linear-gradient(-60deg, #FF8C61 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px'
            }}
          />

          {/* Floating gradient orbs */}
          <div className="absolute top-0 left-0 w-[900px] h-[900px] rounded-full opacity-30">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-light/20 to-primary-orange/10 rounded-full blur-[100px] animate-float-slow" />
          </div>
          <div className="absolute bottom-0 right-0 w-[700px] h-[700px] rounded-full opacity-25">
            <div className="absolute inset-0 bg-gradient-to-tr from-[#7AC74F]/15 to-primary-light/15 rounded-full blur-[80px] animate-float-slow-reverse" />
          </div>

          {/* Subtle wave pattern */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `
                radial-gradient(circle at 50% 50%, #FF8C61 1px, transparent 1px)
              `,
              backgroundSize: '24px 24px',
              transform: 'rotate(-5deg) scale(1.5)'
            }}
          />
        </div>

        {/* Light rays effect */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[conic-gradient(from_0deg_at_50%_50%,transparent_0deg,white_90deg,transparent_180deg,transparent_360deg)] opacity-40" />
        </div>

        {/* Soft vignette */}
        <div className="absolute inset-0 bg-radial-gradient opacity-30" />
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Section Header */}
        <motion.div
          variants={animations.fadeInUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-16"
        >
          <motion.span 
            className="inline-flex items-center gap-2 text-primary-orange font-medium mb-6 bg-primary-orange/10 px-6 py-2.5 rounded-full text-base border border-primary-orange/20"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400 }}
          >
            ✨ Featured Products
          </motion.span>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-8 relative z-10">
            <span className="inline-block px-2 py-4 bg-clip-text text-transparent bg-gradient-to-r from-primary-orange to-primary-light">
              Our Signature Collection
            </span>
          </h2>
          <p className="font-body text-neutral-600 text-lg md:text-xl max-w-2xl mx-auto">
            Discover our handcrafted selection of mochi donuts, made fresh daily with premium ingredients
          </p>
        </motion.div>

        {/* Products Grid - Optimized rendering */}
        <div className="relative -mx-4 sm:-mx-6 lg:-mx-8">
          <div 
            ref={scrollContainerRef}
            className="flex justify-center flex-wrap gap-6 px-4 sm:px-6 lg:px-8 pt-6 pb-8"
          >
            {featuredItems.map((item, index) => (
              <FeaturedProductItem key={item._id} item={item} index={index} />
            ))}
          </div>
        </div>

        {/* View All Button */}
        <motion.div 
          className="text-center mt-8 relative"
          variants={animations.fadeInUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
        >
          <Link href="/menu">
            <Button 
              size="lg"
              className="relative bg-gradient-to-r from-primary-orange to-primary-light hover:to-primary-orange text-white font-heading px-6 py-3 md:px-8 md:py-4 rounded-full transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 transform-gpu shadow-primary-orange/20"
            >
              <span className="inline-flex items-center gap-2">
                View Full Menu <ArrowRight className="w-4 h-4 transform transition-transform group-hover:translate-x-1" />
              </span>
            </Button>
          </Link>
        </motion.div>
      </div>

      {/* Optimized animations */}
      <style jsx global>{`
        @keyframes float-slow {
          0%, 100% { transform: translateY(0) translateZ(0); }
          50% { transform: translateY(-20px) translateZ(0); }
        }
        @keyframes float-slow-reverse {
          0%, 100% { transform: translateY(0) translateZ(0); }
          50% { transform: translateY(20px) translateZ(0); }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: translateZ(0); }
          50% { opacity: 0.6; transform: translateZ(0); }
        }
        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
          will-change: transform;
        }
        .animate-float-slow-reverse {
          animation: float-slow-reverse 10s ease-in-out infinite;
          will-change: transform;
        }
        .animate-pulse-slow {
          animation: pulse-slow 6s ease-in-out infinite;
          will-change: opacity, transform;
        }
      `}</style>
    </Section>
  );
};

export default FeaturedProducts;